"use client";

import { useEffect, useRef, useState } from "react";
import {
  HelpCircle,
  Search,
  Home,
  Settings,
  Users,
  CreditCard,
  Calendar,
  Bell,
  Shield,
  ArrowUp,
  Users2,
  GraduationCap,
  ArrowRightLeft,
  Bus,
  Wallet,
  ScanFace,
  User,
  CalendarClock,
  Wrench,
  UserCog,
  UserCheck,
  BookOpen,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API_ENDPOINTS } from "../../../constants/config";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import ConfidenceBadge from "./components/ConfidenceBadge";
import FeedbackButtons from "./components/FeedbackButtons";
import RelatedTopics from "./components/RelatedTopics";
import ClassroomMappingFlowchart from "./components/ClassroomMappingFlowchart";
import TransitionFlowchart from "./components/TransitionFlowchart";
import LateFeeSlabVisualizer from "./components/LateFeeSlabVisualizer";

const FAQContent = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(true);
  const [faqsError, setFaqsError] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState("");
  const chatContainerRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([
    {
      id: "bot-welcome",
      role: "assistant",
      content: "Hi 👋 I'm Clynox Bot. Ask me anything about how to use Clynox features.",
    },
  ]);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const { isFeatureEnabled } = useFeatureFlag();

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        setFaqsLoading(true);
        const response = await fetch(API_ENDPOINTS.faqs, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          credentials: "include",
        });
        if (!response.ok) throw new Error("Failed to load FAQs");
        const result = await response.json();
        const data = result?.data?.faqs || [];
        setFaqs(Array.isArray(data) ? data : []);
        setFaqsError("");
      } catch (e) {
        setFaqs([]);
        setFaqsError(e?.message || "Failed to load FAQs");
      } finally {
        setFaqsLoading(false);
      }
    };
    fetchFaqs();
  }, []);


  const submitFeedback = async (questionId, helpful) => {
    if (!questionId || feedbackSubmitting) return;
    try {
      setFeedbackSubmitting(true);
      const response = await fetch(API_ENDPOINTS.chatbotFeedback, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify({ questionId, helpful }),
      });
      if (!response.ok) throw new Error("Failed to submit feedback");
      setFeedbackGiven((prev) => ({ ...prev, [questionId]: true }));
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const scrollChatToTop = () => {
    if (!chatContainerRef.current) return;
    chatContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollChatToBottom = () => {
    if (!chatContainerRef.current) return;
    chatContainerRef.current.scrollTo({
      top: chatContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const sendChat = async (incomingMessage) => {
    const message = (incomingMessage ?? chatInput).trim();
    if (!message || chatLoading) return;

    setChatMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: message }]);
    setChatInput("");
    setChatLoading(true);
    setChatError("");

    try {
      const response = await fetch(API_ENDPOINTS.chatbotAsk, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: JSON.stringify({
          message,
          context: { selectedCategory, language: "english" },
        }),
      });

      if (!response.ok) throw new Error("Chatbot request failed");
      const result = await response.json();
      const data = result?.data || {};
      setChatMessages((m) => [
        ...m,
        {
          id: `b-${Date.now()}`,
          role: "assistant",
          content: data.answer || "I don't have that information in the system.",
          sources: Array.isArray(data.sources) ? data.sources : [],
          relatedTopics: Array.isArray(data.relatedTopics) ? data.relatedTopics : [],
          hasFlowchart: Boolean(data.hasFlowchart),
          hasVisualizer: Boolean(data.hasVisualizer),
          visualizationCategory: data.visualizationCategory || data.category || "",
          confidence: data.confidence || "none",
          suggestions: data.suggestions || false,
          questionId: data.questionId || `q-${Date.now()}`,
        },
      ]);
    } catch (e) {
      setChatError(e?.message || "Chatbot request failed");
      setChatMessages((m) => [
        ...m,
        { id: `b-${Date.now()}`, role: "assistant", content: "I couldn’t fetch an answer right now. Please try again in a moment." },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleRelatedTopicClick = (topic) => {
    setChatInput(topic);
    sendChat(topic);
    scrollChatToBottom();
  };

  const categories = [
    { id: "all", label: "All Questions", icon: <HelpCircle className="h-4 w-4" /> },
    { id: "dashboard", label: "Dashboard", icon: <Home className="h-4 w-4" /> },
    { id: "profile", label: "My Profile", icon: <User className="h-4 w-4" /> },
    { id: "staff-leave", label: "Leave Management", icon: <CalendarClock className="h-4 w-4" /> },
    { id: "staff-attendance", label: "My Attendance", icon: <UserCheck className="h-4 w-4" /> },
    { id: "students", label: "Students", icon: <Users className="h-4 w-4" /> },
    { id: "teachers", label: "Teachers", icon: <Users2 className="h-4 w-4" /> },
    { id: "staff", label: "Staff", icon: <UserCog className="h-4 w-4" /> },
    { id: "teacher-attendance", label: "Teacher Attendance", icon: <Users className="h-4 w-4" /> },
    { id: "registration-faces", label: "Registration Faces", icon: <ScanFace className="h-4 w-4" /> },
    { id: "payroll", label: "Payroll", icon: <Wallet className="h-4 w-4" /> },
    { id: "class-transition", label: "Class Transition", icon: <ArrowRightLeft className="h-4 w-4" /> },
    { id: "classroom-mapping", label: "ClassRoom Mapping", icon: <GraduationCap className="h-4 w-4" /> },
    { id: "fee-management", label: "Fee Management", icon: <CreditCard className="h-4 w-4" /> },
    { id: "bus-routes", label: "Bus Routes", icon: <Bus className="h-4 w-4" /> },
    { id: "vendor", label: "Vendor", icon: <Wrench className="h-4 w-4" /> },
    { id: "assets", label: "Assets", icon: <Wrench className="h-4 w-4" /> },
    { id: "inventory", label: "Inventory", icon: <Wrench className="h-4 w-4" /> },
    { id: "customer-ledger", label: "Customer Ledger", icon: <BookOpen className="h-4 w-4" /> },
    { id: "announcements", label: "Announcements", icon: <Bell className="h-4 w-4" /> },
    { id: "holiday", label: "Holiday", icon: <Calendar className="h-4 w-4" /> },
    { id: "alert", label: "Alert Management", icon: <Shield className="h-4 w-4" /> },
    { id: "settings", label: "Settings", icon: <Settings className="h-4 w-4" /> },
  ];

  const getUserFromStorage = () => {
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };
  const user = getUserFromStorage();
  const userPermissions = user?.permissions || [];
  const isStaffWithScopedPermissions = user?.role === "staff" && userPermissions.length > 0;
  const READ_WRITE_CATEGORY_IDS = ["students", "teachers", "staff", "inventory", "vendor", "assets"];

  const hasCategoryPermission = (categoryId) => {
    if (categoryId === "all") return true;
    if (!isStaffWithScopedPermissions) return true;
    if (categoryId === "profile" || categoryId === "staff-leave" || categoryId === "staff-attendance") return true;
    if (categoryId === "settings") return userPermissions.includes("settings") || userPermissions.some((p) => p.startsWith("settings-"));
    if (categoryId === "staff" && userPermissions.includes("settings-admin")) return true;
    if (categoryId === "inventory" && ["issue", "purchase", "edit", "history", "analytics"].some((a) => userPermissions.includes(`inventory:${a}`))) return true;
    if (READ_WRITE_CATEGORY_IDS.includes(categoryId)) return userPermissions.includes(`${categoryId}:read`) || userPermissions.includes(`${categoryId}:write`);
    return userPermissions.includes(categoryId);
  };

  const isCategoryEnabledBySettings = (categoryId) => {
    if (categoryId === "all") return true;
    if (categoryId === "profile" || categoryId === "staff-leave" || categoryId === "staff-attendance") {
      return user?.role === "staff";
    }
    if (categoryId === "settings") return true;
    const map = {
      "bus-routes": isFeatureEnabled("busManagement"),
      "fee-management": isFeatureEnabled("feeManagement"),
      teachers: isFeatureEnabled("teachers"),
      students: isFeatureEnabled("students"),
      dashboard: isFeatureEnabled("dashboard"),
      "class-transition": isFeatureEnabled("classTransition"),
      "classroom-mapping": isFeatureEnabled("classroomMapping"),
      staff: isFeatureEnabled("staff"),
      "teacher-attendance": isFeatureEnabled("clockInOutAttendance") || isFeatureEnabled("manualAttendance"),
      "registration-faces": isFeatureEnabled("webcamAttendance"),
      payroll: isFeatureEnabled("salary"),
      vendor: isFeatureEnabled("vendorManagement"),
      assets: isFeatureEnabled("assetManagement"),
      inventory: isFeatureEnabled("inventory"),
      "customer-ledger": isFeatureEnabled("inventory"),
      announcements: isFeatureEnabled("announcements"),
      holiday: isFeatureEnabled("holidays"),
      alert: isFeatureEnabled("alerts"),
    };
    return map[categoryId] ?? false;
  };

  const canShowCategory = (categoryId) => hasCategoryPermission(categoryId) && isCategoryEnabledBySettings(categoryId);
  const isFaqVisible = (faq) => !!(faq?.question || "").trim() && !!(faq?.answer || "").trim() && !!(faq?.category || "").trim();

  const accessibleFaqs = faqs.filter(isFaqVisible).filter((faq) => canShowCategory(faq.category));
  const filteredFaqs = accessibleFaqs.filter((faq) => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const allowedCategories = categories.filter((cat) => canShowCategory(cat.id));
  const categoryCount = allowedCategories.map((cat) => ({
    ...cat,
    count: cat.id === "all" ? accessibleFaqs.length : accessibleFaqs.filter((faq) => faq.category === cat.id).length,
  }));
  const allCategory = categoryCount.find((cat) => cat.id === "all");
  const categoriesWithData = categoryCount.filter((cat) => cat.id !== "all" && cat.count > 0).sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
  const categoriesForSelect = allCategory ? [allCategory, ...categoriesWithData] : categoriesWithData;
  const quickQuestions = filteredFaqs.slice(0, 8);

  useEffect(() => {
    if (selectedCategory === "all") return;
    if (!allowedCategories.some((c) => c.id === selectedCategory)) setSelectedCategory("all");
  }, [selectedCategory, allowedCategories]);

  useEffect(() => {
    scrollChatToTop();
  }, [selectedCategory]);

  useEffect(() => {
    scrollChatToBottom();
  }, [chatMessages]);

  const resolveMessageVisualizationType = (message) => {
    const rawCategory = (message?.visualizationCategory || "").toLowerCase();
    const content = (message?.content || "").toLowerCase();
    const normalized = rawCategory || content;
    if ((message?.hasFlowchart || normalized.includes("flowchart")) && (normalized.includes("classroom") || normalized.includes("mapping"))) return "classroom";
    if ((message?.hasFlowchart || normalized.includes("flowchart")) && (normalized.includes("transition") || normalized.includes("promotion"))) return "transition";
    if ((message?.hasVisualizer || normalized.includes("late fee") || normalized.includes("slab")) && (normalized.includes("fee") || normalized.includes("fees"))) return "fees";
    return "";
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="w-full h-full overflow-hidden bg-slate-50/60">
      <div className="max-w mx-auto h-full flex flex-col">
        <div className="mb-6 rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 via-white to-indigo-50 p-5 sm:p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-white rounded-xl border border-blue-100 shadow-sm">
                <HelpCircle className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Help Bot</h1>
                <p className="text-sm text-gray-600 mt-1">Ask questions conversationally and get guided help for common Clynox school admin tasks.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 h-[calc(100vh-160px)] min-h-0">
          <div className="min-h-0">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden h-full flex flex-col min-h-0">
              <div className="border-b border-gray-100 px-4 sm:px-6 py-4 bg-gradient-to-r from-slate-50 to-white">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-blue-600" />
                    <h2 className="font-semibold text-gray-900">Conversation</h2>
                  </div>
                  {selectedCategory !== "all" && (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                      {allowedCategories.find((c) => c.id === selectedCategory)?.label || "Category"}
                    </Badge>
                  )}
                </div>
              </div>

              <div ref={chatContainerRef} className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 py-5 space-y-4 bg-slate-50/60 scroll-smooth">
                {chatMessages.map((m) => {
                  const vizType = m.role === "assistant" ? resolveMessageVisualizationType(m) : "";
                  return (
                    <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                      <div className="max-w-[70%] md:max-w-[60%]">
                        {m.role === "assistant" && m.confidence && (
                          <div className="flex items-center gap-2 mb-2">
                            <ConfidenceBadge confidence={m.confidence} />
                            {m.suggestions && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border bg-purple-100 text-purple-800 border-purple-200">
                                <Search className="h-3 w-3" />
                                Suggestions
                              </span>
                            )}
                          </div>
                        )}

                        <div className={`rounded-2xl px-4 py-3 text-sm whitespace-pre-line shadow-sm ${m.role === "user" ? "bg-blue-600 text-white" : "bg-white text-gray-900 border border-gray-200"}`}>
                          {m.content}
                        </div>

                        {m.role === "assistant" && vizType === "classroom" && <div className="mt-4"><ClassroomMappingFlowchart /></div>}
                        {m.role === "assistant" && vizType === "transition" && <div className="mt-4"><TransitionFlowchart /></div>}
                        {m.role === "assistant" && vizType === "fees" && <div className="mt-4"><LateFeeSlabVisualizer /></div>}

                        {m.role === "assistant" && (
                          <>
                            <RelatedTopics topics={m.relatedTopics} onTopicClick={handleRelatedTopicClick} />
                            {m.questionId && m.questionId !== "bot-welcome" && (
                              <FeedbackButtons questionId={m.questionId} onFeedback={submitFeedback} feedbackGiven={feedbackGiven[m.questionId]} />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}

                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="max-w-[70%] md:max-w-[60%] rounded-2xl px-4 py-3 text-sm shadow-sm bg-white text-gray-700 border border-gray-200">Typing...</div>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 sm:p-4">
                {chatError && <div className="text-xs text-red-600 mb-2">{chatError}</div>}
                <div className="flex items-center gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendChat();
                      }
                    }}
                    placeholder="Ask how to do something..."
                    className="h-11 text-sm rounded-xl"
                    disabled={chatLoading}
                  />
                  <Button onClick={() => sendChat()} disabled={chatLoading || !chatInput.trim()} className="h-11 rounded-xl px-5">
                    {chatLoading ? "..." : "Send"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="min-h-0">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden h-full flex flex-col min-h-0">
              <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-white border-b border-gray-100">
                <h3 className="font-semibold text-gray-900">Help Panel</h3>
                <p className="text-xs text-gray-500 mt-0.5">Suggestions update by category and search.</p>
              </div>

              <div className="p-4 space-y-5 h-full overflow-y-auto min-h-0">
                <div>
                  <label htmlFor="faq-search" className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Search Suggestions</label>
                  <Input id="faq-search" type="text" placeholder="Search FAQ suggestions..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="mt-2 h-10 text-sm border-gray-200 rounded-lg" />
                </div>

                <div>
                  <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-2">Quick Questions</p>
                  {faqsLoading ? (
                    <div className="text-sm text-gray-500">Loading suggestions...</div>
                  ) : faqsError ? (
                    <div className="text-sm text-red-600">{faqsError}</div>
                  ) : quickQuestions.length === 0 ? (
                    <div className="text-sm text-gray-500">No matching suggestions.</div>
                  ) : (
                    <div className="space-y-2">
                      {quickQuestions.map((faq) => {
                        const faqId = faq._id || faq.id;
                        return (
                          <button
                            key={faqId}
                            type="button"
                            onClick={() => {
                              setChatInput(faq.question);
                              sendChat(faq.question);
                              scrollChatToBottom();
                            }}
                            className="w-full text-left rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:border-blue-200 hover:shadow-sm transition-all duration-200"
                          >
                            {faq.question}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-2">Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {categoriesForSelect.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedCategory === category.id ? "bg-blue-600 text-white shadow-sm" : "bg-slate-100 text-gray-700 hover:bg-slate-200"}`}
                      >
                        <span>{category.label}</span>
                        <span className={selectedCategory === category.id ? "text-white/90" : "text-gray-500"}>({category.count})</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showBackToTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 z-50 group"
            aria-label="Back to top"
          >
            <ArrowUp className="h-5 w-5 group-hover:scale-110 transition-transform" />
          </button>
        )}
      </div>
    </div>
  );
};

export default FAQContent;
